'use client'

import { useCallback, useEffect, useState } from 'react'
import { useSnapshot } from 'valtio'

import GuidePlayerModal from '@/app/help/GuidePlayerModal'
import { closeNotif, startNotif } from '@/components/DynamicForm/util/notifier'
import Heading from '@/components/Heading/Heading'
import store from '@/store'
import { getErrorMessage } from '@/utils/errorMessages'

import OnboardingProgress from './components/OnboardingProgress'
import OnboardingStepCard from './components/OnboardingStepCard'
import { useOnboarding } from './OnboardingProvider'

type GetStartedPageProps = {
  embedded?: boolean
}

const GetStartedPage = ({ embedded = false }: GetStartedPageProps) => {
  const { currentUser } = useSnapshot(store)
  const {
    groups,
    steps,
    signals,
    completedCount,
    totalCount,
    progress,
    isComplete,
    isLoading,
    currentStep,
    lastCheckedAt,
    refresh,
  } = useOnboarding()
  const [expandedStepId, setExpandedStepId] = useState<string | null>(currentStep?.id || null)
  const [selectedGuideSectionId, setSelectedGuideSectionId] = useState<string | null>(null)

  useEffect(() => {
    if (currentStep?.id) {
      setExpandedStepId(currentStep.id)
    }
  }, [currentStep?.id])

  const openRouteInNewTab = useCallback((route: string) => {
    const url = new URL(route, window.location.origin)
    const storeName = currentUser?.selectedCompany?.store_name

    if (storeName && !url.searchParams.has('storeName')) {
      url.searchParams.set('storeName', storeName)
    }

    window.open(url.toString(), '_blank', 'noopener,noreferrer')
  }, [currentUser?.selectedCompany?.store_name])

  const handleRefreshProgress = useCallback(async () => {
    const notificationId = startNotif('Checking setup progress...')

    try {
      if (!currentUser?.selectedCompany?.id) {
        throw new Error('Select a store before checking setup progress.')
      }

      const refreshedSignals = await refresh()
      if (!refreshedSignals) {
        throw new Error('Setup progress check was interrupted. Please try again.')
      }

      const refreshedCompletedCount = steps.filter((step) => refreshedSignals[step.signalId] === true).length
      const refreshedProgress = Math.round((refreshedCompletedCount / steps.length) * 100)

      closeNotif(notificationId, `Setup progress checked: ${refreshedProgress}% complete.`)
    } catch (error) {
      closeNotif(notificationId, getErrorMessage(error, 'Failed to check setup progress.'), {
        type: 'error',
      })
    }
  }, [currentUser?.selectedCompany?.id, refresh, steps])

  return (
    <div className="flex min-h-full w-full flex-col bg-white">
      <GuidePlayerModal sectionId={selectedGuideSectionId} onClose={() => setSelectedGuideSectionId(null)} />

      {!embedded && (
        <Heading
          title="Get Started"
          description="A guided checklist for preparing your store, product content, pricing, and customer experience."
        />
      )}

      <main className="flex flex-col gap-xxl pr-xxl pb-xxl">
        <OnboardingProgress
          completedCount={completedCount}
          totalCount={totalCount}
          progress={progress}
          isComplete={isComplete}
          isLoading={isLoading}
          storeName={currentUser?.selectedCompany?.name}
          lastCheckedAt={lastCheckedAt}
          onRefresh={handleRefreshProgress}
        />

        <div className="border-b border-gray-200 pb-md">
          <div>
            <h2 className="text-title-lg text-gray-900">Setup checklist</h2>
            <p className="mt-xs text-text-md text-gray-500">
              Start with the highlighted task. Existing work is detected and checked automatically.
            </p>
          </div>
        </div>

        <div className="flex max-w-[1000px] flex-col gap-xxl">
          {groups.map((group, groupIndex) => {
            const groupSteps = steps.filter((step) => step.groupId === group.id)
            const isGroupComplete = groupSteps.length > 0 && groupSteps.every((step) => signals[step.signalId] === true)

            return (
              <section key={group.id} className="flex flex-col gap-sm">
                <div className="flex items-start gap-md px-sm">
                  <span
                    aria-hidden="true"
                    className={`flex h-[32px] w-[32px] shrink-0 items-center justify-center rounded-full text-text-sm font-semibold ${
                      isGroupComplete ? 'bg-primary-500 text-white' : 'bg-gray-900 text-gray-100'
                    }`}
                  >
                    {groupIndex + 1}
                  </span>
                  <div className="min-w-0">
                    <h3 className="text-title-lg text-gray-900">{group.title}</h3>
                    <p className="mt-[2px] text-text-md text-gray-400">{group.description}</p>
                  </div>
                </div>

                <div className="flex flex-col divide-y divide-gray-200 border-y border-gray-200">
                  {groupSteps.map((step) => {
                    return (
                      <OnboardingStepCard
                        key={step.id}
                        step={step}
                        status={signals[step.signalId]}
                        isCurrent={currentStep?.id === step.id}
                        isExpanded={expandedStepId === step.id}
                        isLoading={isLoading}
                        onToggle={() => setExpandedStepId((current) => current === step.id ? null : step.id)}
                        onAction={() => openRouteInNewTab(step.route)}
                        onOpenGuide={() => setSelectedGuideSectionId(step.guideSectionId)}
                      />
                    )
                  })}
                </div>
              </section>
            )
          })}
        </div>
      </main>
    </div>
  )
}

export default GetStartedPage
